import { LegalKind } from "@upsquad/shared";
import { useTranslation } from "react-i18next";
import { LegalFooter } from "@/src/common/components/legal-footer";
import { MarkdownPreview } from "@/src/common/components/markdown-preview";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Spinner } from "@/src/common/components/ui";
import { getSubdomain } from "@/src/common/lib/subdomain";
import NotFoundPage from "@/src/common/pages/not-found-page";
import { useGetPublicCommunityQuery } from "@/src/common/services/community-public-http.service";
import { PublicCommunityHeader } from "@/src/community/components/public-community-header";
import privacyDefault from "@/src/legal/defaults/privacy.md?raw";
import termsDefault from "@/src/legal/defaults/terms-of-use.md?raw";
import { useGetPublicCommunityLegalQuery } from "@/src/legal/services/legal-http.service";

interface PublicLegalPageProps {
	kind: LegalKind;
}

const DEFAULT_MARKDOWN: Record<LegalKind, string> = {
	[LegalKind.PRIVACY]: privacyDefault,
	[LegalKind.TERMS_OF_USE]: termsDefault,
};

export default function PublicLegalPage({ kind }: PublicLegalPageProps) {
	const { t } = useTranslation("legal");
	const subdomain = getSubdomain();
	const { data, isLoading, isError } = useGetPublicCommunityLegalQuery({ slug: subdomain ?? "", kind }, { skip: !subdomain });
	const { data: community } = useGetPublicCommunityQuery(subdomain ?? "", { skip: !subdomain });

	const title = kind === LegalKind.PRIVACY ? t("privacy_title") : t("terms_of_use_title");
	const source = data ? (data.content ?? DEFAULT_MARKDOWN[kind]) : null;

	if (!subdomain || isError) return <NotFoundPage />;

	if (isLoading || !data) {
		return (
			<>
				<PageHelmet title={title} />
				<div className="flex min-h-screen items-center justify-center">
					<Spinner />
				</div>
			</>
		);
	}

	return (
		<>
			<PageHelmet title={title} />
			<div className="flex min-h-screen flex-col bg-background">
				{community && <PublicCommunityHeader communityName={community.name} communityImage={community.image ?? null} />}
				<main className="mx-auto w-full max-w-3xl flex-1 px-6 py-16">
					<h1 className="mb-10 text-4xl font-semibold text-foreground">{title}</h1>
					{data.is_fallback && <p className="mb-8 text-sm text-foreground/60 italic">{t("fallback_disclaimer")}</p>}
					<MarkdownPreview source={source} />
				</main>
				<LegalFooter communityName={community?.name} />
			</div>
		</>
	);
}
